import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Plus, Calendar, Loader2, ChevronDown, ChevronUp, User2, Wallet, Home, Tag } from 'lucide-react';
import { toast } from 'sonner';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TIMEFRAME_LABELS = {
  now: 'Now', '3mo': 'Within 3 months', '6mo': 'Within 6 months',
  '12mo': 'Within 12 months', '12mo_plus': '12+ months',
};

const NewLeadDialog = ({ open, onClose, options, onSuccess, authToken }) => {
  const [loading, setLoading] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    lead_source: 'Website',
    lead_type: 'Purchase',
    priority: 'Warm',
    notes: '',
    next_follow_up_at: '',
    follow_up_method: 'Call',
    // Pre-qualification profile (Iteration A)
    dob: '',
    postcode: '',
    preferred_contact_method: '',
    preferred_contact_time: '',
    timeframe: '',
    household_income: '',
    deposit_saved: '',
    employment_type: '',
    property_target_price_min: '',
    property_target_price_max: '',
    property_target_location: '',
    property_target_type: '',
    tags: [],
  });

  const addTag = () => {
    const t = (tagInput || '').trim();
    if (t && !formData.tags.includes(t)) {
      setFormData((f) => ({ ...f, tags: [...f.tags, t] }));
    }
    setTagInput('');
  };
  const removeTag = (t) => setFormData((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Lead name is required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        next_follow_up_at: formData.next_follow_up_at || null,
        household_income: formData.household_income ? parseFloat(formData.household_income) : null,
        deposit_saved: formData.deposit_saved ? parseFloat(formData.deposit_saved) : null,
        property_target_price_min: formData.property_target_price_min ? parseFloat(formData.property_target_price_min) : null,
        property_target_price_max: formData.property_target_price_max ? parseFloat(formData.property_target_price_max) : null,
        dob: formData.dob || null,
        postcode: formData.postcode || null,
        preferred_contact_method: formData.preferred_contact_method || null,
        preferred_contact_time: formData.preferred_contact_time || null,
        timeframe: formData.timeframe || null,
        employment_type: formData.employment_type || null,
        property_target_location: formData.property_target_location || null,
        property_target_type: formData.property_target_type || null,
        tags: formData.tags.length > 0 ? formData.tags : null,
      };
      const response = await fetch(`${backendUrl}/api/nurture/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to create lead');

      toast.success('Lead created successfully!');
      onSuccess();
      onClose();
      setFormData({
        name: '', phone: '', email: '', lead_source: 'Website',
        lead_type: 'Purchase', priority: 'Warm', notes: '',
        next_follow_up_at: '', follow_up_method: 'Call',
        dob: '', postcode: '', preferred_contact_method: '', preferred_contact_time: '',
        timeframe: '', household_income: '', deposit_saved: '', employment_type: '',
        property_target_price_min: '', property_target_price_max: '',
        property_target_location: '', property_target_type: '', tags: [],
      });
      setShowProfile(false);
    } catch (err) {
      toast.error('Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-600" />
            Add New Lead
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Lead Name *</label>
              <Input
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                placeholder="John Smith"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Phone Number</label>
              <Input
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                placeholder="0400 000 000"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Email Address</label>
            <Input
              type="email"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              placeholder="john@example.com"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Lead Source</label>
              <Select value={formData.lead_source} onValueChange={v => setFormData({...formData, lead_source: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.lead_sources?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Lead Type</label>
              <Select value={formData.lead_type} onValueChange={v => setFormData({...formData, lead_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.lead_types?.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Priority</label>
              <Select value={formData.priority} onValueChange={v => setFormData({...formData, priority: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.priorities?.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Notes / Summary</label>
            <Textarea
              value={formData.notes}
              onChange={e => setFormData({...formData, notes: e.target.value})}
              placeholder="Summary of conversation or enquiry details..."
              rows={3}
            />
          </div>

          {/* Collapsible: Pre-qualification profile (Iteration A) */}
          <button
            type="button"
            onClick={() => setShowProfile((v) => !v)}
            className="w-full flex items-center justify-between text-left text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/70 rounded-lg px-3 py-2 transition-colors"
            data-testid="toggle-prequal-profile"
          >
            <span className="flex items-center gap-2">
              <User2 className="w-4 h-4" /> Pre-qualification details (optional, boosts lead score)
            </span>
            {showProfile ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showProfile && (
            <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-700">Date of birth</label>
                  <Input type="date" value={formData.dob}
                    onChange={e => setFormData({ ...formData, dob: e.target.value })}
                    data-testid="lead-dob" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Postcode</label>
                  <Input value={formData.postcode}
                    onChange={e => setFormData({ ...formData, postcode: e.target.value })}
                    placeholder="7000" maxLength={4}
                    data-testid="lead-postcode" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-700">Preferred contact</label>
                  <Select value={formData.preferred_contact_method} onValueChange={v => setFormData({ ...formData, preferred_contact_method: v })}>
                    <SelectTrigger data-testid="lead-pref-contact-method"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {(options.contact_methods || ['call', 'email', 'sms']).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Best time</label>
                  <Select value={formData.preferred_contact_time} onValueChange={v => setFormData({ ...formData, preferred_contact_time: v })}>
                    <SelectTrigger data-testid="lead-pref-contact-time"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {(options.contact_times || ['morning', 'afternoon', 'evening', 'anytime']).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 flex items-center gap-1"><Calendar className="w-3 h-3" /> Timeframe</label>
                  <Select value={formData.timeframe} onValueChange={v => setFormData({ ...formData, timeframe: v })}>
                    <SelectTrigger data-testid="lead-timeframe"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {(options.timeframes || Object.keys(TIMEFRAME_LABELS)).map(t => (
                        <SelectItem key={t} value={t}>{(options.timeframe_labels || TIMEFRAME_LABELS)[t] || t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-2">
                <h5 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Financial snapshot (rough only — no Fact Find)</h5>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-700">Household income (AUD)</label>
                    <Input type="number" value={formData.household_income}
                      onChange={e => setFormData({ ...formData, household_income: e.target.value })}
                      placeholder="120000" data-testid="lead-household-income" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-700">Deposit saved (AUD)</label>
                    <Input type="number" value={formData.deposit_saved}
                      onChange={e => setFormData({ ...formData, deposit_saved: e.target.value })}
                      placeholder="80000" data-testid="lead-deposit-saved" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-700">Employment</label>
                    <Select value={formData.employment_type} onValueChange={v => setFormData({ ...formData, employment_type: v })}>
                      <SelectTrigger data-testid="lead-employment-type"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {(options.employment_types || ['PAYG', 'Self-Employed', 'Casual', 'Contract', 'Other']).map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <h5 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5"><Home className="w-3.5 h-3.5" /> Property target</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-700">Price min (AUD)</label>
                    <Input type="number" value={formData.property_target_price_min}
                      onChange={e => setFormData({ ...formData, property_target_price_min: e.target.value })}
                      placeholder="600000" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-700">Price max (AUD)</label>
                    <Input type="number" value={formData.property_target_price_max}
                      onChange={e => setFormData({ ...formData, property_target_price_max: e.target.value })}
                      placeholder="800000" data-testid="lead-property-price-max" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <label className="text-xs font-medium text-slate-700">Location</label>
                    <Input value={formData.property_target_location}
                      onChange={e => setFormData({ ...formData, property_target_location: e.target.value })}
                      placeholder="Hobart, Tas / Sandy Bay" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-700">Property type</label>
                    <Select value={formData.property_target_type} onValueChange={v => setFormData({ ...formData, property_target_type: v })}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {(options.property_types || ['House', 'Townhouse', 'Apartment', 'Unit', 'Land', 'Other']).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <h5 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Tags</h5>
                <div className="flex gap-2">
                  <Input value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    placeholder="e.g. self-employed, rentvestor"
                    data-testid="lead-tag-input" />
                  <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {formData.tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-500/40 rounded-full px-2 py-0.5 text-xs">
                        {t}
                        <button type="button" onClick={() => removeTag(t)} className="hover:text-rose-500">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="p-4 bg-slate-50 dark:bg-slate-800/40 rounded-lg">
            <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Schedule First Follow-Up
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-600">Date & Time</label>
                <Input
                  type="datetime-local"
                  value={formData.next_follow_up_at}
                  onChange={e => setFormData({...formData, next_follow_up_at: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Method</label>
                <Select value={formData.follow_up_method} onValueChange={v => setFormData({...formData, follow_up_method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.follow_up_methods?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Lead
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { NewLeadDialog };
export default NewLeadDialog;
